/**
 * Provisioning (SCIM 2.0) for the current workspace (migration 986).
 *
 * The identity provider pushes: it adds, changes, deactivates and removes
 * people through the `scim` edge function, with a token made here. There is no
 * "sync now" — the provider sends each change as it happens.
 *
 * What an administrator does here: copy the base URL, create a token (shown
 * once — only its hash is kept), revoke a token, and read who was provisioned
 * and what happened.
 */

import { useCallback, useEffect, useState } from 'react';
import { Panel, Callout, CopyButton, DataTable, TextInput, type Column } from '../../../../components/ds';
import { btnPrimary, btnDanger, status } from '../../../../components/mono/primitives';
import {
  createScimToken,
  listIdentityEvents,
  listProvisionedPeople,
  listScimTokens,
  revokeScimToken,
  scimBaseUrl,
  type IdentityEvent,
  type ProvisionedPerson,
  type ScimToken,
} from '../../../../lib/identityService';

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}


export function ProvisioningPanel({ workspaceId, workspaceName, tenantId }: { workspaceId: string; workspaceName: string; tenantId: string | null }) {
  const [tokens, setTokens] = useState<ScimToken[]>([]);
  const [people, setPeople] = useState<ProvisionedPerson[]>([]);
  const [events, setEvents] = useState<IdentityEvent[]>([]);
  const [label, setLabel] = useState('');
  const [newToken, setNewToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const baseUrl = scimBaseUrl();

  const load = useCallback(async () => {
    try {
      const [t, p, e] = await Promise.all([listScimTokens(workspaceId), listProvisionedPeople(workspaceId), listIdentityEvents(workspaceId, tenantId)]);
      setTokens(t);
      setPeople(p);
      setEvents(e);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read provisioning.');
    }
  }, [workspaceId, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      setNewToken(await createScimToken(workspaceId, label));
      setLabel('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create a token.');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (t: ScimToken) => {
    if (!window.confirm(`Revoke the token ending ${t.hint}? The identity provider using it stops being able to add or remove people at once.`)) return;
    setBusy(true);
    setError(null);
    try {
      await revokeScimToken(t.id);
      // A token just revoked is no longer worth copying.
      setNewToken((shown) => (shown?.endsWith(t.hint) ? null : shown));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not revoke it.');
    } finally {
      setBusy(false);
    }
  };

  const live = tokens.filter((t) => !t.revokedAt);

  const tokenColumns: Column<ScimToken>[] = [
    { key: 'hint', header: 'Token', render: (t) => <span className="font-mono">scim_…{t.hint}</span> },
    { key: 'label', header: 'For', render: (t) => t.label ?? '—' },
    { key: 'created', header: 'Created', render: (t) => when(t.createdAt) },
    { key: 'used', header: 'Last used', render: (t) => when(t.lastUsedAt) },
    {
      key: 'state',
      header: '',
      align: 'right',
      render: (t) =>
        t.revokedAt ? (
          <span {...status('neutral')}>Revoked {new Date(t.revokedAt).toLocaleDateString()}</span>
        ) : (
          <button type="button" {...btnDanger()} disabled={busy} onClick={() => void revoke(t)}>
            Revoke
          </button>
        ),
    },
  ];

  const peopleColumns: Column<ProvisionedPerson>[] = [
    { key: 'name', header: 'Person', render: (p) => p.displayName },
    { key: 'userName', header: 'User name', render: (p) => <span className="font-mono text-xs">{p.userName}</span> },
    { key: 'active', header: 'State', render: (p) => <span {...status(p.active ? 'green' : 'neutral')}>{p.active ? 'Member' : 'Deactivated'}</span> },
    { key: 'updated', header: 'Last change', render: (p) => when(p.updatedAt) },
  ];

  return (
    <div className="space-y-6 mt-6">
      <Panel label="Provisioning · SCIM 2.0" title={`Your identity provider adds and removes people in ${workspaceName}`}>
        <div className="space-y-4 text-sm text-[var(--text-sec)]">
          <p>
            In Okta or Microsoft Entra ID, add Priorities.ai as an app with SCIM provisioning, and give it the base URL and a token from here.
            From then on the provider sends each change as it happens: a person assigned to the app becomes a member of this workspace; a
            person deactivated or unassigned is taken out of it. Their login and their work stay. Groups are not provisioned yet.
          </p>
          <div>
            <p className="mb-1.5 font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]">SCIM base URL</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate border border-[var(--rule)] bg-[var(--surface)] px-3 py-2 font-mono text-xs text-[var(--text-pri)]">{baseUrl}</code>
              <CopyButton text={baseUrl} label="Copy" />
            </div>
          </div>
        </div>
      </Panel>

      <Panel label="Tokens" title="Provisioning tokens" count={live.length}>
        <div className="space-y-4">
          {newToken && (
            <Callout tone="mustard" title="Copy this token now — it is not shown again" action={<CopyButton text={newToken} label="Copy token" />}>
              <code className="block break-all font-mono text-xs text-[var(--text-pri)]">{newToken}</code>
              <span className="mt-2 block">Paste it into the provider as the bearer token (secret token). Only a fingerprint of it is kept here.</span>
            </Callout>
          )}
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[14rem] flex-1">
              <TextInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Which provider it is for, e.g. Okta" aria-label="Which provider the token is for" />
            </div>
            <button type="button" {...btnPrimary()} disabled={busy} onClick={() => void create()}>
              Create token
            </button>
          </div>
          {tokens.length > 0 && <DataTable rows={tokens} columns={tokenColumns} getRowKey={(t) => t.id} />}
        </div>
      </Panel>

      <Panel label="People" title="Provisioned by the identity provider" count={people.length}>
        {people.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)]">Nobody yet. People appear here when the provider sends them.</p>
        ) : (
          <DataTable rows={people} columns={peopleColumns} getRowKey={(p) => p.userId} />
        )}
      </Panel>

      <Panel label="Log" title="What happened" count={events.length}>
        {events.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)]">Nothing yet.</p>
        ) : (
          <ul className="divide-y divide-[var(--rule)]">
            {events.map((e) => (
              <li key={e.id} className="flex items-start gap-3 py-2 text-sm">
                <span {...status(e.ok ? 'green' : 'coral', 'mt-0.5 shrink-0')}>{e.ok ? 'Done' : 'Refused'}</span>
                <span className="flex-1 text-[var(--text-sec)]">{e.detail ?? e.kind}</span>
                <span className="shrink-0 text-xs text-[var(--text-ter)]">{when(e.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {error && <Callout tone="coral">{error}</Callout>}
    </div>
  );
}
